import { memo, type RefObject } from 'react';

import { Button } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentMentionListSentinel } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionListSentinel';
import { workspaceAgentSkillTriggerMenuSecondary } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerChrome';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';

const WORKSPACE_AGENT_SKILLS_CATALOG_SENTINEL_ROOT_MARGIN = '0px 0px 48px 0px';

export type WorkspaceAgentSkillsCatalogListFooterProps = {
  hasNextPage: boolean;
  /** When false, only the scroll sentinel renders (for use inside a listbox). */
  includeStatusUi?: boolean;
  /** When false, hides the loading row but can still show pagination error/retry (e.g. actions menu skeleton). */
  includeLoadingMoreStatus?: boolean;
  /** When false, hides pagination error/retry. Defaults to `includeStatusUi`. */
  includePaginationErrorStatus?: boolean;
  /** When false, only loading/error UI renders (sibling of the listbox). */
  includeScrollSentinel?: boolean;
  isFetchNextPageError: boolean;
  isFetchingNextPage: boolean;
  listRef: RefObject<HTMLElement | null>;
  onRequestNextPage: () => void;
  onRetryNextPage: () => void;
};

export const WorkspaceAgentSkillsCatalogListFooter = memo(
  ({
    hasNextPage,
    includeLoadingMoreStatus,
    includePaginationErrorStatus,
    includeScrollSentinel = true,
    includeStatusUi = true,
    isFetchNextPageError,
    isFetchingNextPage,
    listRef,
    onRequestNextPage,
    onRetryNextPage,
  }: WorkspaceAgentSkillsCatalogListFooterProps) => {
    const showLoadingMoreStatus = includeLoadingMoreStatus ?? includeStatusUi;
    const showPaginationErrorStatus = includePaginationErrorStatus ?? includeStatusUi;
    const showSentinel = includeScrollSentinel && hasNextPage && !isFetchNextPageError;
    const showLoading = showLoadingMoreStatus && isFetchingNextPage;
    const showError = showPaginationErrorStatus && isFetchNextPageError;

    if (!showSentinel && !showLoading && !showError) {
      return null;
    }

    return (
      <div className="flex shrink-0 flex-col gap-1 pt-1">
        {showSentinel ? (
          <WorkspaceAgentMentionListSentinel
            isFetching={isFetchingNextPage}
            listRef={listRef}
            onFetch={onRequestNextPage}
            rootMargin={WORKSPACE_AGENT_SKILLS_CATALOG_SENTINEL_ROOT_MARGIN}
          />
        ) : null}
        {showLoading ? (
          <div
            aria-live="polite"
            className={cn('flex items-center justify-center gap-2 py-2', workspaceAgentSkillTriggerMenuSecondary())}
            data-testid="WORKSPACE_AGENT_SKILLS_CATALOG_LIST_LOADING_MORE"
            role="status"
          >
            <Spinner aria-hidden className="size-4 shrink-0 text-gray-9" />
            Loading more skills…
          </div>
        ) : null}
        {showError ? (
          <div
            className={cn('flex flex-col items-center gap-2 px-1.5 py-2', workspaceAgentSkillTriggerMenuSecondary())}
            data-testid="WORKSPACE_AGENT_SKILLS_CATALOG_LIST_LOAD_ERROR"
            role="alert"
          >
            <span>Could not load more skills.</span>
            <Button
              appearance="ghost"
              data-testid="WORKSPACE_AGENT_SKILLS_CATALOG_LIST_RETRY_BUTTON"
              onClick={onRetryNextPage}
              onMouseDown={preserveWorkspaceAgentComposerFocus}
              size="small"
              type="button"
            >
              Try again
            </Button>
          </div>
        ) : null}
      </div>
    );
  },
);

WorkspaceAgentSkillsCatalogListFooter.displayName = 'WorkspaceAgentSkillsCatalogListFooter';
